import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { useFieldArray, useForm, useWatch, type Resolver } from "react-hook-form";

import { defaultHealthDetailsFormValues } from "@/D2C/features/health-quote/redux/health-quote/healthQuoteSlice";
import {
  Button,
  RadioInput,
  RepeatablePeopleSection,
  SelectInput,
  TextInput,
} from "@/shared/components";
import DateInput from "@/shared/components/DateInput";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import { formatCurrencyValue } from "@/shared/utils/amountFormat";
import { healthDetailsSchema } from "@/shared/validation/healthQuoteSchemas";
import {
  CURRENT_INSURER_OPTIONS,
  HEALTH_COVERAGE_AMOUNT_OPTIONS,
  HEALTH_COVERAGE_TYPE_OPTIONS,
  HEALTH_POLICY_TYPE_OPTIONS,
  MAX_MEMBERS,
  RELATIONSHIP_OPTIONS,
  SUM_INSURED_OPTIONS,
  TENURE_OPTIONS,
  YES_NO_OPTIONS,
} from "../../common/static";
import { HealthDetailsFormValues } from "../../redux/type";

const today = new Date().toISOString().split("T")[0];

const createPrimaryMember = () => ({
  name: "",
  relationship: "self" as const,
  dob: "",
  preExistingCondition: false,
});

export const HealthDetailsStep = forwardRef<
  StepFormHandle<HealthDetailsFormValues>,
  StepFormComponentProps<
    HealthDetailsFormValues,
    {
      layoutClassName?: string;
      additionalTravellersLayout?: "stacked" | "grid";
      showMemberDetailsTitle?: boolean;
      portingPolicyLayoutClassName?: string;
      livePrimaryMemberName?: string;
      isReadOnly?: boolean;
    }
  >
>(function HealthDetailsStep(
  {
    defaultValues,
    layoutClassName,
    additionalTravellersLayout = "stacked",
    showMemberDetailsTitle = true,
    portingPolicyLayoutClassName,
    livePrimaryMemberName,
    isReadOnly = false,
  },
  ref
) {
  const { sharedPersonalDetails } = usePosterReducers();
  const form = useForm<HealthDetailsFormValues>({
    resolver: zodResolver(healthDetailsSchema) as Resolver<
      HealthDetailsFormValues,
      any,
      HealthDetailsFormValues
    >,
    defaultValues: defaultValues ?? defaultHealthDetailsFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "members",
  });

  const coverageType = useWatch({
    control: form.control,
    name: "coverageType",
  });
  const coverageAmount = useWatch({
    control: form.control,
    name: "coverageAmount",
  });
  const policyType = useWatch({
    control: form.control,
    name: "policyType",
  });

  const coverageAmountOptions = useMemo(() => {
    const baseOptions = HEALTH_COVERAGE_AMOUNT_OPTIONS.map((option) => ({
      label: option.label,
      value: option.value,
    }));

    const normalizedCoverageAmount = String(coverageAmount || "").replace(/[^\d]/g, "");
    if (!normalizedCoverageAmount) {
      return baseOptions;
    }

    const alreadyExists = baseOptions.some((option) => option.value === normalizedCoverageAmount);
    if (alreadyExists) {
      return baseOptions;
    }

    const displayAmount = Number(normalizedCoverageAmount);
    return [
      ...baseOptions,
      {
        label: Number.isNaN(displayAmount)
          ? normalizedCoverageAmount
          : formatCurrencyValue(displayAmount, {
              inputCurrency: LOCALISATION_CONFIG.currencyCode,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
        value: normalizedCoverageAmount,
      },
    ];
  }, [coverageAmount]);

  const primaryMemberName = [sharedPersonalDetails.firstName, sharedPersonalDetails.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  useEffect(() => {
    form.reset(defaultValues ?? defaultHealthDetailsFormValues);
  }, [defaultValues, form]);

  useEffect(() => {
    const currentPolicyType = form.getValues("policyType");

    if (!currentPolicyType) {
      form.setValue("policyType", "newPolicy", {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    }
  }, [form]);

  useEffect(() => {
    const currentMembers = form.getValues("members");

    if (!currentMembers?.length) {
      replace([createPrimaryMember()]);
      return;
    }

    const [primaryMember, ...remainingMembers] = currentMembers;

    if (!primaryMember) {
      replace([createPrimaryMember()]);
      return;
    }

    if (primaryMember.relationship !== "self") {
      replace([
        {
          ...primaryMember,
          relationship: "self",
        },
        ...remainingMembers,
      ]);
      return;
    }

    if (coverageType === "individual" && currentMembers.length > 1) {
      replace([primaryMember]);
      return;
    }
  }, [append, coverageType, form, replace]);

  useEffect(() => {
    if (policyType === "porting_policy") {
      return;
    }

    form.setValue("currentInsurer", "", { shouldDirty: true, shouldValidate: true });
    form.setValue("policyExpiryDate", "", { shouldDirty: true, shouldValidate: true });
    form.setValue("sumInsured", "", { shouldDirty: true, shouldValidate: true });
    form.setValue("claimsInLast3Years", "", { shouldDirty: true, shouldValidate: true });
  }, [form, policyType]);

  useEffect(() => {
    const resolvedPrimaryMemberName =
      livePrimaryMemberName !== undefined ? livePrimaryMemberName.trim() : primaryMemberName;

    if (!resolvedPrimaryMemberName && livePrimaryMemberName === undefined) {
      return;
    }

    const currentPrimaryName = form.getValues("members.0.name");

    if (currentPrimaryName === resolvedPrimaryMemberName) {
      return;
    }

    form.setValue("members.0.name", resolvedPrimaryMemberName, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form, livePrimaryMemberName, primaryMemberName]);

  useImperativeHandle(ref, () => ({
    submit: async () => form.trigger(undefined, { shouldFocus: true }),
    getValues: () => form.getValues(),
  }));

  const handleAddMember = () => {
    append({
      name: "",
      relationship: "",
      dob: "",
      preExistingCondition: false,
    });
  };

  const canAddMember =
    (coverageType === "family" || coverageType === "family_floater") && fields.length < MAX_MEMBERS;

  const formLayoutClassName = layoutClassName ?? "grid grid-cols-1 gap-4 sm:grid-cols-2";
  const resolvedPortingPolicyLayoutClassName =
    portingPolicyLayoutClassName ?? "col-span-full grid grid-cols-1 gap-4 sm:grid-cols-2";

  return (
    <fieldset disabled={isReadOnly} className="space-y-6 border-0 p-0">
      <div className={formLayoutClassName}>
        <RadioInput
          control={form.control}
          name="policyType"
          label="Policy Type"
          options={HEALTH_POLICY_TYPE_OPTIONS}
          required
        />

        <SelectInput
          control={form.control}
          name="coverageAmount"
          label="Coverage Amount"
          placeholder="Select Coverage Amount"
          options={coverageAmountOptions}
          required
        />
        <RadioInput
          control={form.control}
          name="coverageType"
          label="Coverage Type"
          options={HEALTH_COVERAGE_TYPE_OPTIONS}
          required
        />

        {policyType === "porting_policy" ? (
          <div className={resolvedPortingPolicyLayoutClassName}>
            <SelectInput
              control={form.control}
              name="currentInsurer"
              label="Current Insurer"
              placeholder="Select Current Insurer"
              options={CURRENT_INSURER_OPTIONS.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
              searchable
              required
            />
            <DateInput
              control={form.control}
              name="policyExpiryDate"
              label="Policy Expiry Date"
              placeholder="DD/MM/YYYY"
              yearOrder="asc"
              required
              min={today}
            />
            <SelectInput
              control={form.control}
              name="sumInsured"
              label="Sum Insured"
              placeholder="Select Sum Insured"
              options={SUM_INSURED_OPTIONS.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
              required
            />
            <RadioInput
              control={form.control}
              name="claimsInLast3Years"
              label="Claims in Last 3 Years"
              options={YES_NO_OPTIONS}
              required
            />
          </div>
        ) : null}

        <DateInput
          control={form.control}
          name="startDate"
          label="Cover Start Date"
          required
          min={today}
          yearOrder="asc"
        />
        <SelectInput
          control={form.control}
          name="policy_tenure"
          label="Policy Tenure"
          placeholder="Select policy term"
          options={TENURE_OPTIONS}
          required
        />

        <div className="col-span-full">
          <RepeatablePeopleSection
            title={showMemberDetailsTitle ? "Member Details" : ""}
            helperText={
              showMemberDetailsTitle
                ? coverageType === "family" || coverageType === "family_floater"
                  ? `You can add up to ${MAX_MEMBERS} covered members.`
                  : "Individual cover keeps just the primary member."
                : undefined
            }
            fields={fields}
            canAddItem={canAddMember}
            additionalTravellersLayout={additionalTravellersLayout}
            additionalTravellersGridClassName="grid gap-4 md:grid-cols-2 lg:grid-cols-2"
            onAddItem={handleAddMember}
            onRemoveItem={remove}
            renderItem={({
              field,
              index,
              isPrimary,
              canAddItem: canAdd,
              canRemoveItem,
              onAddItem,
              onRemoveItem,
            }) => {
              const dob = form.watch(`members.${index}.dob`);
              const hasAge = !!dob;

              return (
                <div key={field?.id} className="flex flex-row items-start gap-4">
                  <TextInput
                    control={form.control}
                    name={`members.${index}.name`}
                    label={isPrimary ? "Primary Member" : `Member ${index + 1}`}
                    placeholder={isPrimary ? "Primary member" : `Member ${index + 1}`}
                    required
                    className={hasAge ? "w-full max-w-[140px]" : "w-full"}
                  />

                  <SelectInput
                    control={form.control}
                    name={`members.${index}.relationship`}
                    label="Relationship"
                    placeholder="Select"
                    options={
                      isPrimary
                        ? RELATIONSHIP_OPTIONS
                        : RELATIONSHIP_OPTIONS.filter((option) => option.value !== "self")
                    }
                    disabled={isPrimary}
                    hideChevron={isPrimary}
                    required
                    className={`
                      ${hasAge ? "w-full max-w-[110px]" : "w-full"}
                      ${isPrimary ? "cursor-not-allowed" : ""}
                    `}
                  />

                  <DateInput
                    control={form.control}
                    name={`members.${index}.dob`}
                    label="Date of Birth"
                    placeholder="DD/MM/YYYY"
                    max={today}
                    showAge
                    yearOrder="desc"
                    required
                    className="w-full"
                  />

                  {isPrimary && canAdd ? (
                    <Button
                      variant="secondary"
                      className=" mt-6 flex h-9 w-9 items-center justify-center rounded-xl px-0 text-lg leading-none"
                      aria-label="Add member"
                      onClick={onAddItem}
                    >
                      <span className="-mt-0.5">+</span>
                    </Button>
                  ) : null}

                  {!isPrimary && canRemoveItem ? (
                    <Button
                      variant="secondary"
                      className="mt-6 flex h-9 w-9 items-center justify-center rounded-xl px-0 text-lg leading-none"
                      aria-label={`Remove member ${index + 1}`}
                      onClick={onRemoveItem}
                    >
                      <span className="-mt-0.5">-</span>
                    </Button>
                  ) : null}
                </div>
              );
            }}
          />
        </div>
      </div>
    </fieldset>
  );
});
